import React, { useState, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
    Box,
    Button,
    Chip,
    Grid,
    MenuItem,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import dayjs from "dayjs";
import { tokens } from "../../assets/theme";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
    useWeekTemplates,
    useMachines,
    useAssignTemplate,
    useRemoveAssignedTemplate,
} from "../../hooks/useWeekSchedule";
import AxionControls from "../../components/controls/AxionControls";
import { pushErrorMessage, pushNotification } from "../../utils/notifications";
import messages from "./components/messages";
import { FormattedMessage, useIntl } from "react-intl";

function AssignToMachinesIndex() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { formatMessage } = useIntl();
    const REMOVE_TEMPLATE_OPTION = "__remove__";

    const [companyKey] = useLocalStorage("companyKey", 1);

    const [selectedTemplateId, setSelectedTemplateId] = useState("");
    const [selectedMachineIds, setSelectedMachineIds] = useState([]);
    const [weekStart, setWeekStart] = useState(
        dayjs().startOf("week").add(1, "day").format("YYYY-MM-DD")
    );
    const [validTo, setValidTo] = useState("");

    const { data: templates = [] } = useWeekTemplates(companyKey);
    const { data: machines = [] } = useMachines(companyKey);
    const assignTemplateMutation = useAssignTemplate();
    const removeTemplateMutation = useRemoveAssignedTemplate();

    const [showBackdrop, setShowBackdrop] = useState(false);

    const queryClient = useQueryClient();

    const workspaceBg =
        theme.palette.mode === "dark" ? colors.primary[700] : colors.grey[100];

    const dateTimeIconSx =
        theme.palette.mode === "dark"
            ? {
                "& input::-webkit-calendar-picker-indicator": {
                    filter: "invert(1)",
                },
            }
            : {};

    const weekEndDefault = useMemo(
        () => dayjs(weekStart).add(6, "day").format("YYYY-MM-DD"),
        [weekStart]
    );

    const handleAssign = async () => {
        if (!selectedTemplateId || selectedMachineIds.length === 0) return;

        setShowBackdrop(true);

        try {
            const vf = dayjs(weekStart).format("YYYY-MM-DD");
            const vt = validTo ? dayjs(validTo).format("YYYY-MM-DD") : dayjs(weekEndDefault).format("YYYY-MM-DD");

            if (selectedTemplateId === REMOVE_TEMPLATE_OPTION) {
                for (const machineId of selectedMachineIds) {
                    await removeTemplateMutation.mutateAsync({
                        machineId,
                        fromDate: vf,
                        toDate: vt ?? vf, // om tomt validTo: tolka som single-day eller sätt default här
                    });
                }

                pushNotification("Grundschema har tagits bort för valda maskiner och datum.");
            } else {
                for (const machineId of selectedMachineIds) {
                    await assignTemplateMutation.mutateAsync({
                        machineId,
                        weekTemplateId: Number(selectedTemplateId),
                        validFrom: vf,
                        validTo: vt,
                    });
                }

                pushNotification("Schemat har kopplats till maskinerna.");
            }
            await queryClient.invalidateQueries({
                queryKey: ["calendarMatrix", companyKey],
                exact: false,
            });
        } catch (error) {
            console.error(error);
            pushErrorMessage("Kunde inte uppdatera grundschema för maskinerna.");
        } finally {
            setShowBackdrop(false);
        }
    };

    const isAssigning = assignTemplateMutation.isPending || removeTemplateMutation.isPending || showBackdrop;

    const ALL_MACHINES_OPTION = "__all__";

    const sortedMachines = useMemo(
        () => [...machines].sort((a, b) => a.machineName.localeCompare(b.machineName)),
        [machines]
    );

    const allMachineIds = useMemo(
        () => sortedMachines.map((m) => m.id),
        [sortedMachines]
    );

    const allMachinesSelected =
        allMachineIds.length > 0 &&
        allMachineIds.every((id) => selectedMachineIds.includes(id));

    const handleMachineChange = (e) => {
        const value = e.target.value;

        if (value.includes(ALL_MACHINES_OPTION)) {
            setSelectedMachineIds(allMachinesSelected ? [] : allMachineIds);
            return;
        }

        setSelectedMachineIds(
            typeof value === "string"
                ? value.split(",").map(Number)
                : value
        );
    };

    return (
        <>
            <Box
                sx={{
                    p: 2,
                    backgroundColor: workspaceBg,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                }}
            >
                <Typography variant="h5" gutterBottom>
                    <FormattedMessage {...messages.assignWeekTemplateToMachines} />
                </Typography>

                <Grid container spacing={2} mb={1}>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label={formatMessage(messages.fromDateMonday)}
                            InputLabelProps={{ shrink: true }}
                            value={weekStart}
                            onChange={(e) => setWeekStart(e.target.value)}
                            sx={dateTimeIconSx}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            fullWidth
                            type="date"
                            label={formatMessage(messages.toDate)}
                            InputLabelProps={{ shrink: true }}
                            value={validTo || ""}
                            placeholder={weekEndDefault}
                            onChange={(e) => setValidTo(e.target.value)}
                            sx={dateTimeIconSx}
                            size="small"
                        />
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            label={formatMessage(messages.weekTemplateToAssign)}
                            value={selectedTemplateId}
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">
                                <em><FormattedMessage {...messages.selectWeekTemplate} /></em>
                            </MenuItem>
                            <MenuItem value={REMOVE_TEMPLATE_OPTION}>
                                <em><FormattedMessage {...messages.removeWeekTemplate} /></em>
                            </MenuItem>
                            {templates.map((t) => (
                                <MenuItem key={t.id} value={t.id}>
                                    <Box
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 1,
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 14,
                                                height: 14,
                                                borderRadius: 0.5,
                                                backgroundColor:
                                                    t.templateColor || "#888",
                                                border:
                                                    "1px solid rgba(255,255,255,0.6)",
                                            }}
                                        />
                                        {t.name}
                                    </Box>
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <TextField
                            select
                            fullWidth
                            label={formatMessage(messages.machinesToAssign)}
                            SelectProps={{
                                multiple: true,
                                renderValue: (selected) => {
                                    if (selected.length === allMachineIds.length && allMachineIds.length > 0) {
                                        return <Typography variant="body2"><FormattedMessage {...messages.allMachinesSelected} /></Typography>;
                                    }

                                    return (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexWrap: "wrap",
                                                gap: 0.5,
                                            }}
                                        >
                                            {selected.map((id) => {
                                                const m = sortedMachines.find((x) => x.id === id);
                                                return (
                                                    <Chip
                                                        key={id}
                                                        label={m ? m.machineName : id}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#799c4a',
                                                            color: '#fff'
                                                        }}

                                                    />
                                                );
                                            })}
                                        </Box>
                                    );
                                },
                                MenuProps: {
                                    PaperProps: {
                                        sx: {
                                            "& .MuiMenuItem-root": {
                                                borderRadius: 1,
                                                mx: 0.5,
                                                my: 0.25,
                                            },
                                        },
                                    },
                                },
                            }}
                            value={selectedMachineIds}
                            onChange={handleMachineChange}
                            size="small"
                        >
                            <MenuItem
                                value={ALL_MACHINES_OPTION}
                                sx={{
                                    fontStyle: "italic",
                                    fontWeight: 700,
                                    backgroundColor: allMachinesSelected ? "#799c4a" : "transparent",
                                    "&:hover": {
                                        backgroundColor: allMachinesSelected
                                            ? "rgba(144, 202, 249, 0.30)"
                                            : theme.palette.action.hover,
                                    },
                                }}
                            >
                                <FormattedMessage {...messages.allMachines} />
                            </MenuItem>

                            {sortedMachines.map((m) => {
                                const isSelected = selectedMachineIds.includes(m.id);

                                return (
                                    <MenuItem
                                        key={m.id}
                                        value={m.id}
                                        sx={{
                                            backgroundColor: isSelected ? "#799c4a" : "transparent",
                                            fontWeight: isSelected ? 700 : 400,
                                            borderLeft: isSelected
                                                ? `4px solid ${theme.palette.primary.main}`
                                                : "4px solid transparent",
                                            "&:hover": {
                                                backgroundColor: isSelected
                                                    ? "rgba(144, 202, 249, 0.30)"
                                                    : theme.palette.action.hover,
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "#799c4a",
                                            },
                                            "&.Mui-selected:hover": {
                                                backgroundColor: "rgba(144, 202, 249, 0.30)"
                                            },
                                        }}
                                    >
                                        {m.machineName}
                                    </MenuItem>
                                );
                            })}
                        </TextField>
                    </Grid>
                </Grid>

                <Box mt={2}>
                    <Button
                        variant="contained"
                        onClick={handleAssign}
                        disabled={!selectedTemplateId || selectedMachineIds.length === 0 || isAssigning}
                        sx={{
                            backgroundColor: colors.gray[500],
                            "&:hover": { backgroundColor: colors.gray[700] },
                            color: "#ffffff",
                            textTransform: "none",
                            fontSize: "0.9rem",
                        }}
                    >
                        {selectedTemplateId === REMOVE_TEMPLATE_OPTION
                            ? <FormattedMessage {...messages.unassignWeekTemplateFromMachines} />
                            : <FormattedMessage {...messages.assignWeekTemplateToMachines} />
                        }
                    </Button>
                </Box>
            </Box >

            <AxionControls.AxionBackdrop open={showBackdrop} />
        </>
    );
}

export default AssignToMachinesIndex;